import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent,
} from "react"
import { ChevronLeft, LocateFixed, Menu, Minus, Plus } from "lucide-react"

import type { PrintProfileFieldLayout, PrintProfileFieldOffsets } from "shared"

import {
  defaultCalibrationFields,
  protocolV2FieldExamples,
  protocolV2FieldLabels,
  protocolV2PrintedFields,
} from "@/components/documentPreviewFields"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

type PageSize = { width: number; height: number }
type Point = { x: number; y: number }
type Rect = { x: number; y: number; width: number; height: number }
type FieldContentMode = "labels" | "examples"
type FieldAutoSizeSource = "saved" | "examples"

// The scan is portrait: its width-to-height ratio is 9877 / 14027.
// Keep the canvas page in the same aspect ratio so object-contain does not
// letterbox the scan into a landscape wrapper (which shifts every field).
const protocolPageRatio = 9877 / 14027
const minZoom = 0.25
const maxZoom = 2
const zoomStep = 0.1
const a4WidthMm = 210
const a4HeightMm = 297
// CSS's reference-pixel definition (1in = 96px, so 1mm = 96/25.4px) holds for
// both screen and print rendering — it's what lets the print stylesheet
// address the page in physical units (`mm`, `pt`, `@page { size: A4 }`) and
// get the same proportions the screen canvas shows. Sizing the on-screen page
// to this same physical scale — instead of whatever happens to fit the
// viewport — means every px-based measurement below (field boxes, example
// text size) already matches what print produces; the viewport constraint is
// handled entirely by the `zoom` transform, not by shrinking the page itself.
const pxPerMm = 96 / 25.4
const pageWidthPx = a4WidthMm * pxPerMm
// Match the scan's own aspect ratio (see below) rather than literal a4HeightMm
// so the page box doesn't letterbox the scan under `object-contain`.
const pageHeightPx = pageWidthPx / protocolPageRatio
const canonicalPageSize: PageSize = { width: pageWidthPx, height: pageHeightPx }
const offsetMinMm = -25
const offsetMaxMm = 25
const offsetStepMm = 1
const elementScaleMin = 0.8
const elementScaleMax = 1.2
const elementScaleStep = 0.01
const multilineFieldIds = new Set([
  "inspectionPlace",
  "inspectionEntity",
  "vehiclePurpose",
])
const fieldTextExclusionZones: Record<string, Rect[]> = {
  vehiclePurpose: [
    {
      x: 0.078,
      y: 0.622,
      width: 0.39,
      height: 0.077,
    },
  ],
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

type DraggableResizableFieldProps = {
  editable: boolean
  field: PrintProfileFieldLayout
  autoSizeText?: string
  fieldContentMode: FieldContentMode
  selected: boolean
  showBorders: boolean
  exampleFontSizePt: number
  exampleValue: string
  pageSize: PageSize
  zoom: number
  offsetX: number
  offsetY: number
  elementScale: number
  onSelect: () => void
  onChange: (field: PrintProfileFieldLayout) => void
}

function DraggableResizableField({
  editable,
  field,
  autoSizeText,
  fieldContentMode,
  selected,
  showBorders,
  exampleFontSizePt,
  exampleValue,
  pageSize,
  zoom,
  offsetX,
  offsetY,
  elementScale,
  onSelect,
  onChange,
}: DraggableResizableFieldProps) {
  const nodeRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const autoSizeMeasureRef = useRef<HTMLSpanElement>(null)
  const [isContentOverflowing, setIsContentOverflowing] = useState(false)
  const [measuredAutoSize, setMeasuredAutoSize] = useState<PageSize | null>(
    null
  )
  const interactionRef = useRef<{
    kind: "drag" | "resize"
    pointerId: number
    startClientX: number
    startClientY: number
    startField: PrintProfileFieldLayout
  } | null>(null)
  const fieldLabel = protocolV2FieldLabels[field.id] ?? field.id
  const isExampleMode = fieldContentMode === "examples"
  const fieldText = isExampleMode ? exampleValue : fieldLabel
  const isSingleLine = !multilineFieldIds.has(field.id)
  const shouldAutoSize = isSingleLine && Boolean(autoSizeText)
  const exampleFontSizePx = (exampleFontSizePt * 96 * elementScale) / 72
  const labelFontSizePx = (10 * elementScale) / zoom
  const savedWidth = Math.max(1, field.width * pageSize.width * elementScale)
  const savedHeight = Math.max(1, field.height * pageSize.height * elementScale)
  const x =
    pageSize.width / 2 +
    (field.x * pageSize.width - pageSize.width / 2) * elementScale +
    offsetX
  const y =
    pageSize.height / 2 +
    (field.y * pageSize.height - pageSize.height / 2) * elementScale +
    offsetY
  const scaledPageLeft = (pageSize.width * (1 - elementScale)) / 2 + offsetX
  const scaledPageTop = (pageSize.height * (1 - elementScale)) / 2 + offsetY
  const scaledPageRight = scaledPageLeft + pageSize.width * elementScale
  const scaledPageBottom = scaledPageTop + pageSize.height * elementScale
  const width =
    shouldAutoSize && measuredAutoSize
      ? Math.max(
          1,
          Math.min(Math.ceil(measuredAutoSize.width) + 10, scaledPageRight - x)
        )
      : savedWidth
  const height =
    shouldAutoSize && measuredAutoSize
      ? Math.max(
          1,
          Math.min(Math.ceil(measuredAutoSize.height) + 3, scaledPageBottom - y)
        )
      : savedHeight
  const visibleWidth = width / (pageSize.width * elementScale)
  const visibleHeight = height / (pageSize.height * elementScale)
  const showBorderLabel = showBorders
  const textExclusionZones = (fieldTextExclusionZones[field.id] ?? [])
    .map((zone) => {
      const fieldRight = field.x + field.width
      const fieldBottom = field.y + field.height
      const zoneRight = zone.x + zone.width
      const zoneBottom = zone.y + zone.height
      const overlapLeft = Math.max(field.x, zone.x)
      const overlapTop = Math.max(field.y, zone.y)
      const overlapRight = Math.min(fieldRight, zoneRight)
      const overlapBottom = Math.min(fieldBottom, zoneBottom)

      if (overlapRight <= overlapLeft || overlapBottom <= overlapTop) {
        return null
      }

      return {
        marginTop: ((overlapTop - field.y) / field.height) * 100,
        height: ((overlapBottom - overlapTop) / field.height) * 100,
        width: ((overlapRight - field.x) / field.width) * 100,
      }
    })
    .filter(
      (zone): zone is { marginTop: number; height: number; width: number } =>
        Boolean(zone)
    )

  useLayoutEffect(() => {
    const measureNode = autoSizeMeasureRef.current
    if (!shouldAutoSize || !measureNode) {
      setMeasuredAutoSize(null)
      return
    }

    let cancelled = false
    const updateAutoSize = () => {
      if (cancelled) return
      setMeasuredAutoSize((current) => {
        const next = {
          width: measureNode.offsetWidth,
          height: measureNode.offsetHeight,
        }
        return current?.width === next.width && current.height === next.height
          ? current
          : next
      })
    }

    updateAutoSize()
    void document.fonts?.ready.then(updateAutoSize)

    return () => {
      cancelled = true
    }
  }, [autoSizeText, exampleFontSizePx, shouldAutoSize])

  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content || !isExampleMode) {
      setIsContentOverflowing(false)
      return
    }

    const updateOverflowState = () => {
      setIsContentOverflowing(
        content.scrollWidth > content.clientWidth ||
          content.scrollHeight > content.clientHeight
      )
    }

    updateOverflowState()
    const observer = new ResizeObserver(updateOverflowState)
    observer.observe(content)
    void document.fonts?.ready.then(updateOverflowState)

    return () => observer.disconnect()
  }, [exampleFontSizePt, fieldText, height, isExampleMode, width])

  const moveWithKeyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!editable) return
    // Arrow keys typed while editing the example-value input/textarea move
    // the text cursor, not the field box.
    const targetTag = (event.target as HTMLElement).tagName
    if (targetTag === "INPUT" || targetTag === "TEXTAREA") return

    const delta = {
      ArrowLeft: { x: -1 / a4WidthMm, y: 0 },
      ArrowRight: { x: 1 / a4WidthMm, y: 0 },
      ArrowUp: { x: 0, y: -1 / a4HeightMm },
      ArrowDown: { x: 0, y: 1 / a4HeightMm },
    }[event.key]

    if (!delta) return

    event.preventDefault()
    event.stopPropagation()
    const minX = -scaledPageLeft / (pageSize.width * elementScale)
    const maxX =
      (pageSize.width - scaledPageLeft) / (pageSize.width * elementScale) -
      visibleWidth
    const minY = -scaledPageTop / (pageSize.height * elementScale)
    const maxY =
      (pageSize.height - scaledPageTop) / (pageSize.height * elementScale) -
      visibleHeight

    onChange({
      ...field,
      x: clamp(field.x + delta.x, minX, maxX),
      y: clamp(field.y + delta.y, minY, maxY),
    })
  }

  const startInteraction = (
    event: PointerEvent<HTMLElement>,
    kind: "drag" | "resize"
  ) => {
    if (!editable || event.button !== 0) return

    onSelect()
    nodeRef.current?.focus()
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    interactionRef.current = {
      kind,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startField: field,
    }
  }

  const updateInteraction = (event: PointerEvent<HTMLElement>) => {
    const interaction = interactionRef.current
    if (!interaction || interaction.pointerId !== event.pointerId) return

    event.preventDefault()
    event.stopPropagation()
    const deltaX =
      (event.clientX - interaction.startClientX) /
      zoom /
      elementScale /
      pageSize.width
    const deltaY =
      (event.clientY - interaction.startClientY) /
      zoom /
      elementScale /
      pageSize.height

    if (interaction.kind === "drag") {
      const minX = -scaledPageLeft / (pageSize.width * elementScale)
      const maxX =
        (pageSize.width - scaledPageLeft) /
          (pageSize.width * elementScale) -
        visibleWidth
      const minY = -scaledPageTop / (pageSize.height * elementScale)
      const maxY =
        (pageSize.height - scaledPageTop) /
          (pageSize.height * elementScale) -
        visibleHeight

      onChange({
        ...interaction.startField,
        x: clamp(interaction.startField.x + deltaX, minX, maxX),
        y: clamp(interaction.startField.y + deltaY, minY, maxY),
      })
      return
    }

    const maxWidth =
      (pageSize.width - scaledPageLeft) / (pageSize.width * elementScale) -
      interaction.startField.x
    const maxHeight =
      (pageSize.height - scaledPageTop) / (pageSize.height * elementScale) -
      interaction.startField.y

    onChange({
      ...interaction.startField,
      width: clamp(interaction.startField.width + deltaX, 0, maxWidth),
      height: clamp(interaction.startField.height + deltaY, 0, maxHeight),
    })
  }

  const stopInteraction = (event: PointerEvent<HTMLElement>) => {
    if (interactionRef.current?.pointerId !== event.pointerId) return

    event.preventDefault()
    event.stopPropagation()
    interactionRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <div
      className="group/preview-field absolute top-0 left-0"
      data-content-overflow={isContentOverflowing}
      data-preview-interactive="true"
      data-selected={selected}
      aria-label={fieldLabel}
      tabIndex={editable ? 0 : undefined}
      onPointerCancel={stopInteraction}
      onPointerDown={(event) => startInteraction(event, "drag")}
      onPointerMove={updateInteraction}
      onPointerUp={stopInteraction}
      onFocus={onSelect}
      onKeyDown={moveWithKeyboard}
      ref={nodeRef}
      style={{ left: x, top: y, width, height }}
    >
      {shouldAutoSize && (
        <span
          aria-hidden="true"
          className="pointer-events-none invisible absolute whitespace-nowrap"
          ref={autoSizeMeasureRef}
          style={{
            fontSize: `${exampleFontSizePx}px`,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {autoSizeText}
        </span>
      )}
      {showBorderLabel && (
        <span
          className={cn(
            "pointer-events-none absolute bottom-full left-0 z-10 bg-background px-1 whitespace-nowrap text-primary"
          )}
          style={{ fontSize: `${labelFontSizePx}px` }}
        >
          {fieldLabel}
        </span>
      )}
      <div
        className={cn(
          "h-full w-full overflow-hidden rounded border px-1 text-left transition duration-200",
          editable ? "cursor-move" : "cursor-default",
          selected && "ring-2 ring-primary ring-offset-1",
          isContentOverflowing
            ? "border-destructive bg-destructive/10 shadow-sm"
            : showBorders
              ? "border-dashed border-primary bg-background/80 shadow-sm"
              : "border-transparent bg-transparent shadow-none hover:border-dashed hover:border-primary",
          isExampleMode ? "text-black" : "text-primary"
        )}
        style={{
          // Labels keep their 10px marker size at every zoom level. Example
          // values are real document text: the page transform scales them
          // together with the scan.
          fontSize: isExampleMode
            ? `${exampleFontSizePx}px`
            : `${labelFontSizePx}px`,
          lineHeight: isExampleMode ? 1.2 : undefined,
        }}
        ref={contentRef}
      >
        {isExampleMode &&
          textExclusionZones.map((zone, index) => (
            <span
              aria-hidden="true"
              className="pointer-events-none float-left clear-left"
              key={`spacer-${index}`}
              style={{
                height: `${zone.marginTop}%`,
                width: 0,
              }}
            />
          ))}
        {isExampleMode &&
          textExclusionZones.map((zone, index) => (
            <span
              aria-hidden="true"
              className="pointer-events-none float-left clear-left"
              key={`wrap-${index}`}
              style={{
                height: `${zone.height}%`,
                width: `${zone.width}%`,
              }}
            />
          ))}
        {(isExampleMode || !showBorderLabel) && (
          <span className={cn("block", isSingleLine && "whitespace-nowrap")}>
            {fieldText}
          </span>
        )}
      </div>
      {editable && (
        <span
          aria-label={`Змінити розмір поля: ${fieldLabel}`}
          className={cn(
            "absolute right-0 bottom-0 z-10 size-5 translate-x-1 translate-y-1 cursor-se-resize touch-none rounded-sm bg-transparent transition-opacity duration-200",
            showBorders
              ? "opacity-100"
              : "opacity-0 group-hover/preview-field:opacity-100"
          )}
          onPointerCancel={stopInteraction}
          onPointerDown={(event) => startInteraction(event, "resize")}
          onPointerMove={updateInteraction}
          onPointerUp={stopInteraction}
          role="button"
        >
          <span
            className={cn(
              "pointer-events-none absolute right-1 bottom-1 size-2 border-r-2 border-b-2",
              isExampleMode ? "border-black/50" : "border-white/80"
            )}
          />
        </span>
      )}
    </div>
  )
}

type PrintFieldProps = {
  field: PrintProfileFieldLayout
  fieldContentMode: FieldContentMode
  exampleFontSizePt: number
  exampleValue: string
  offsetXPercent: number
  offsetYPercent: number
  elementScale: number
}

// Static, non-interactive counterpart to `DraggableResizableField` used only
// in the `@media print` snapshot. It positions fields with percentages of
// the printed page instead of `pageSize`/`zoom`/`pan` pixel math, so the
// printed layout is correct regardless of how the browser scales the page —
// `pageSize` itself is derived from the on-screen viewport and has no
// meaning on paper. It never draws the calibration border/label chrome —
// that's an on-screen editing aid, not something the physical blank prints.
function PrintField({
  field,
  fieldContentMode,
  exampleFontSizePt,
  exampleValue,
  offsetXPercent,
  offsetYPercent,
  elementScale,
}: PrintFieldProps) {
  const fieldLabel = protocolV2FieldLabels[field.id] ?? field.id
  const isExampleMode = fieldContentMode === "examples"
  const fieldText = isExampleMode ? exampleValue : fieldLabel
  const isSingleLine = !multilineFieldIds.has(field.id)
  const left = 50 + (field.x - 0.5) * 100 * elementScale + offsetXPercent
  const top = 50 + (field.y - 0.5) * 100 * elementScale + offsetYPercent
  const width = field.width * 100 * elementScale
  const height = field.height * 100 * elementScale
  // `pt` is a physical unit, so it prints at the same size regardless of
  // screen DPI/zoom — the on-screen marker/example sizes are converted from
  // their px equivalents (10px marker ≈ 7.5pt at 96dpi).
  const fontSizePt = (isExampleMode ? exampleFontSizePt : 7.5) * elementScale
  // Mirrors `DraggableResizableField`'s measured auto-size: single-line
  // fields grow to fit their content instead of using the saved box, so a
  // long typed test value isn't clipped by a narrower calibrated width.
  // Done with plain CSS (`white-space: nowrap`, no explicit width/height)
  // rather than a JS measurement, since this block is only laid out by the
  // browser once printing actually starts — a `ResizeObserver` never fires
  // on a `display: none` element. The three multiline fields keep the
  // saved preset box instead (print-profiles.md §69's stated exception).
  const autoSize = isSingleLine && Boolean(fieldText)

  return (
    <div
      className={cn(
        "absolute overflow-hidden px-1 text-left",
        autoSize && "whitespace-nowrap",
        isExampleMode ? "text-black" : "text-primary"
      )}
      style={{
        left: `${left}%`,
        top: `${top}%`,
        width: autoSize ? undefined : `${width}%`,
        height: autoSize ? undefined : `${height}%`,
        maxWidth: autoSize ? `${Math.max(0, 100 - left)}%` : undefined,
        fontSize: `${fontSizePt}pt`,
        lineHeight: isExampleMode ? 1.2 : undefined,
      }}
    >
      {fieldText}
    </div>
  )
}

export type DocumentPreviewCanvasProps = {
  scanSrc?: string | null
  vehicle?: string
  result?: string
  /** Shows the draggable/resizable field markers used by calibration. */
  calibration?: boolean
  /** Shows field blocks without enabling editing (for read-only profile view). */
  showFieldBlocks?: boolean
  /** Shows the v2 printable-field list beside the page. */
  showFieldList?: boolean
  fields?: PrintProfileFieldLayout[]
  onFieldsChange?: (fields: PrintProfileFieldLayout[]) => void
  showMockContent?: boolean
  /** Enables the label/example segment and example text-size selector. */
  showFieldContentControls?: boolean
  /** Shows the label/example segment when the surrounding controls are enabled. */
  showFieldContentModeControl?: boolean
  /** Initial segment selection for the field content controls. */
  initialFieldContentMode?: "labels" | "examples"
  /** Determines which values auto-size single-line field boxes. */
  fieldAutoSizeSource?: FieldAutoSizeSource
  /** Label for the example/data segment option. */
  examplesLabel?: string
  /** Values shown in "examples" content mode, keyed by field id — defaults to the calibration-screen's own mock examples. */
  exampleValues?: Record<string, string>
  /** Shows the example font-size selector independently from the segment. */
  showFieldFontSizeControl?: boolean
  /** Shows the calibration-only offset and group-scale controls. */
  showCalibrationControls?: boolean
  /** Shows the background-blank visibility toggle. */
  showBlankControl?: boolean
  /** Shared offsets applied to every field block, measured in millimetres. */
  offsets?: PrintProfileFieldOffsets
  onOffsetsChange?: (offsets: PrintProfileFieldOffsets) => void
  /** Shared scale applied to the printed field group, independent of canvas zoom. */
  elementScale?: number
  onElementScaleChange?: (scale: number) => void
  className?: string
}

export function DocumentPreviewCanvas({
  scanSrc,
  vehicle = "Volkswagen Crafter, АА1234КХ",
  result = "позитивний",
  calibration = false,
  showFieldList = calibration,
  fields = defaultCalibrationFields,
  onFieldsChange,
  showMockContent = true,
  showFieldContentControls = calibration,
  showFieldContentModeControl = showFieldContentControls,
  showFieldFontSizeControl = showFieldContentControls,
  showCalibrationControls = calibration,
  initialFieldContentMode = "labels",
  fieldAutoSizeSource = "saved",
  examplesLabel = "Приклад даних",
  exampleValues = protocolV2FieldExamples,
  showBlankControl = true,
  showFieldBlocks = calibration,
  offsets,
  onOffsetsChange,
  elementScale,
  onElementScaleChange,
  className,
}: DocumentPreviewCanvasProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  const [viewportSize, setViewportSize] = useState<PageSize>({
    width: 0,
    height: 0,
  })
  const pageSize = canonicalPageSize
  const [layoutFields, setLayoutFields] = useState(fields)
  const layoutFieldsRef = useRef(fields)
  const [isFieldListExpanded, setIsFieldListExpanded] = useState(true)
  // `null` means "no explicit zoom yet" — derive a fit-to-viewport zoom from
  // the (physically fixed-size) page and the measured viewport instead of
  // seeding it from an effect, so there's no synchronous setState-in-effect
  // render cascade. Once the user (or `updateZoom`) picks a zoom, that value
  // wins until the page unmounts.
  const [zoomOverride, setZoomOverride] = useState<number | null>(null)
  const fitZoom =
    viewportSize.width > 0 && viewportSize.height > 0
      ? clamp(
          Math.min(
            Math.max(viewportSize.width - 40, 100) / pageSize.width,
            Math.max(viewportSize.height - 40, 100) / pageSize.height,
            1
          ),
          minZoom,
          maxZoom
        )
      : 1
  const zoom = zoomOverride ?? fitZoom
  const [fieldContentMode, setFieldContentMode] = useState<FieldContentMode>(
    initialFieldContentMode
  )
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [showFieldBorders, setShowFieldBorders] = useState(true)
  const [exampleFontSizePt, setExampleFontSizePt] = useState(9)
  const [isBlankVisible, setIsBlankVisible] = useState(true)
  const [localOffsets, setLocalOffsets] = useState({ x: 0, y: 0 })
  const [localElementScale, setLocalElementScale] = useState(1)
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const panStartRef = useRef<{
    pointerId: number
    startPoint: Point
    startPan: Point
  } | null>(null)

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const updateViewportSize = () => {
      setViewportSize({
        width: viewport.clientWidth,
        height: viewport.clientHeight,
      })
    }

    updateViewportSize()
    const observer = new ResizeObserver(updateViewportSize)
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [])

  const updateField = (updatedField: PrintProfileFieldLayout) => {
    const nextFields = layoutFieldsRef.current.map((field) =>
      field.id === updatedField.id ? updatedField : field
    )
    layoutFieldsRef.current = nextFields
    setLayoutFields(nextFields)
    onFieldsChange?.(nextFields)
  }

  const resolveExampleValue = (fieldId: string) => exampleValues[fieldId] ?? ""

  const updateZoom = (nextZoom: number) => {
    setZoomOverride(clamp(nextZoom, minZoom, maxZoom))
  }

  const currentOffsets = offsets ?? localOffsets
  const currentElementScale = elementScale ?? localElementScale
  const updateOffset = (axis: "x" | "y", value: number) => {
    const nextOffsets = { ...currentOffsets, [axis]: value }
    if (!offsets) setLocalOffsets(nextOffsets)
    onOffsetsChange?.(nextOffsets)
  }

  const updateElementScale = (value: number) => {
    if (elementScale === undefined) setLocalElementScale(value)
    onElementScaleChange?.(value)
  }

  const formatOffset = (value: number) => `${value > 0 ? "+" : ""}${value} мм`

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return

    setSelectedFieldId(null)
    event.currentTarget.setPointerCapture(event.pointerId)
    panStartRef.current = {
      pointerId: event.pointerId,
      startPoint: { x: event.clientX, y: event.clientY },
      startPan: pan,
    }
    setIsPanning(true)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const panStart = panStartRef.current
    if (!panStart || panStart.pointerId !== event.pointerId) return

    setPan({
      x: panStart.startPan.x + event.clientX - panStart.startPoint.x,
      y: panStart.startPan.y + event.clientY - panStart.startPoint.y,
    })
  }

  const stopPanning = (event: PointerEvent<HTMLDivElement>) => {
    if (panStartRef.current?.pointerId === event.pointerId) {
      panStartRef.current = null
      setIsPanning(false)
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    }
  }

  const contentSize = {
    width: Math.max(viewportSize.width, pageSize.width * zoom + 40),
    height: Math.max(viewportSize.height, pageSize.height * zoom + 40),
  }

  return (
    <div
      className={cn(
        "relative h-full min-h-[32rem] w-full min-w-0 overflow-hidden print:static print:h-auto print:min-h-0 print:w-full print:overflow-visible",
        className
      )}
    >
      <div
        ref={viewportRef}
        className={cn(
          "absolute inset-0 overflow-auto select-none print:hidden",
          isPanning ? "cursor-grabbing" : "cursor-grab"
        )}
        onPointerCancel={stopPanning}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopPanning}
      >
        <div
          className="relative shrink-0"
          style={{ height: contentSize.height, width: contentSize.width }}
        >
          <div
            className="absolute top-1/2 left-1/2"
            style={{
              height: pageSize.height,
              transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "center center",
              width: pageSize.width,
            }}
          >
              <div
                className={cn(
                  "relative size-full overflow-hidden",
                  scanSrc && isBlankVisible
                    ? "border-0 bg-transparent shadow-none"
                    : "border bg-white shadow-sm"
                )}
                ref={pageRef}
              >
                {scanSrc && isBlankVisible && (
                  <img
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 size-full object-contain"
                    src={scanSrc}
                  />
                )}
                {showMockContent && (
                  <div className="relative z-10 h-full p-8 sm:p-10">
                    <h2 className="text-center font-semibold">
                      ПРОТОКОЛ ОБОВ'ЯЗКОВОГО ТЕХНІЧНОГО КОНТРОЛЮ
                    </h2>
                    <p className="mt-8">
                      Транспортний засіб: {vehicle || "не заповнено"}
                    </p>
                    <p className="mt-3">Результат: {result}</p>
                    <div className="mt-20 border-t pt-4 text-sm">
                      Макет документа з mock-даними та профілем принтера
                    </div>
                  </div>
                )}
                {showFieldBlocks && (
                  <div className="absolute inset-0 z-20">
                    {layoutFields
                      .filter((field) => field.enabled !== false)
                      .map((field) => (
                        <DraggableResizableField
                          autoSizeText={
                            fieldAutoSizeSource === "examples"
                              ? resolveExampleValue(field.id)
                              : undefined
                          }
                          editable={calibration}
                          exampleFontSizePt={exampleFontSizePt}
                          exampleValue={resolveExampleValue(field.id)}
                          field={field}
                          fieldContentMode={fieldContentMode}
                          onSelect={() => setSelectedFieldId(field.id)}
                          selected={selectedFieldId === field.id}
                          showBorders={showFieldBorders}
                          key={field.id}
                          onChange={updateField}
                          elementScale={currentElementScale}
                          offsetX={
                            (currentOffsets.x / a4WidthMm) * pageSize.width
                          }
                          offsetY={
                            (currentOffsets.y / a4HeightMm) * pageSize.height
                          }
                          pageSize={pageSize}
                          zoom={zoom}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
        </div>
      </div>
      {showFieldBlocks && (
        <div className="hidden print:block print:w-full">
          <div
            // Match the scan's own aspect ratio here too (see protocolPageRatio's
            // definition above), not the nominal A4 ratio — otherwise
            // `object-contain` letterboxes the scan against this container while
            // PrintField's percentages stay anchored to the container, shifting
            // every field relative to the image only in print. Sized from the
            // physical page HEIGHT (not `width: 100%`) so the box never exceeds
            // one `@page`-sized sheet — `width: 100%` here would compute a
            // height slightly taller than a4HeightMm (the scan is marginally
            // taller-than-A4 relative to its width), spilling a sliver onto a
            // second, near-blank page.
            className="relative mx-auto overflow-hidden"
            style={{
              height: `${a4HeightMm}mm`,
              width: `${a4HeightMm * protocolPageRatio}mm`,
            }}
          >
            {scanSrc && isBlankVisible && (
              <img
                alt=""
                aria-hidden="true"
                className="absolute inset-0 size-full object-contain"
                src={scanSrc}
              />
            )}
            {layoutFields
              .filter((field) => field.enabled !== false)
              .map((field) => (
                <PrintField
                  elementScale={currentElementScale}
                  exampleValue={resolveExampleValue(field.id)}
                  field={field}
                  fieldContentMode={fieldContentMode}
                  key={field.id}
                  offsetXPercent={(currentOffsets.x / a4WidthMm) * 100}
                  offsetYPercent={(currentOffsets.y / a4HeightMm) * 100}
                  exampleFontSizePt={exampleFontSizePt}
                />
              ))}
          </div>
        </div>
      )}
      <div
        className="absolute top-3 right-3 z-40 flex justify-end print:hidden"
        data-preview-interactive="true"
        onPointerDown={(event) => event.stopPropagation()}
      >
        {showFieldContentControls && (
          <div className="flex w-64 max-w-[calc(100vw-1.5rem)] flex-col items-stretch gap-2">
            {showFieldContentModeControl && (
              <div
                aria-label="Вміст блоків полів"
                className="inline-flex h-8 w-full items-center gap-0.5 rounded-md border border-neutral-600 bg-neutral-900 p-0.5"
                role="group"
              >
                <Button
                  aria-pressed={fieldContentMode === "labels"}
                  className={cn(
                    "h-7 min-w-0 flex-1 px-2 text-xs",
                    fieldContentMode !== "labels" &&
                      "!border-transparent !bg-transparent !text-neutral-300 hover:!bg-neutral-800/80 hover:!text-white"
                  )}
                  onClick={() => setFieldContentMode("labels")}
                  variant={fieldContentMode === "labels" ? "default" : "ghost"}
                >
                  Назви полів
                </Button>
                <Button
                  aria-pressed={fieldContentMode === "examples"}
                  className={cn(
                    "h-7 min-w-0 flex-1 px-2 text-xs",
                    fieldContentMode !== "examples" &&
                      "!border-transparent !bg-transparent !text-neutral-300 hover:!bg-neutral-800/80 hover:!text-white"
                  )}
                  onClick={() => setFieldContentMode("examples")}
                  variant={
                    fieldContentMode === "examples" ? "default" : "ghost"
                  }
                >
                  {examplesLabel}
                </Button>
              </div>
            )}
            {showFieldFontSizeControl && (
              <div
                className={cn(
                  "flex h-8 w-full items-center gap-1 rounded-md border border-neutral-600 bg-neutral-900 px-2 text-white",
                  showFieldContentModeControl && "mt-4"
                )}
              >
                <label
                  className="text-xs whitespace-nowrap"
                  htmlFor="preview-font-size"
                >
                  Розмір шрифту:
                </label>
                <Select
                  onValueChange={(value) => setExampleFontSizePt(Number(value))}
                  value={String(exampleFontSizePt)}
                >
                  <SelectTrigger
                    className="h-7 min-w-16 border-0 bg-transparent px-1 text-xs text-white shadow-none focus-visible:ring-0 [&_svg]:text-neutral-300"
                    id="preview-font-size"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border border-neutral-600 bg-neutral-900 text-white">
                    {Array.from(
                      { length: 13 },
                      (_, index) => 8 + index * 0.5
                    ).map((fontSize) => (
                        <SelectItem
                          className="focus:bg-neutral-700 focus:text-white"
                          key={fontSize}
                          value={String(fontSize)}
                        >
                          {fontSize}pt
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <label
              className={cn(
                "flex h-8 w-full items-center gap-2 rounded-md border border-neutral-600 bg-neutral-900 px-2 text-xs text-white",
                !showFieldFontSizeControl &&
                  showFieldContentModeControl &&
                  "mt-4"
              )}
            >
              <Checkbox
                aria-label="Показувати рамки полів"
                checked={showFieldBorders}
                onCheckedChange={(checked) =>
                  setShowFieldBorders(checked === true)
                }
              />
              <span>Показувати рамки полів</span>
            </label>
            {showBlankControl && (
              <label className="flex h-8 w-full items-center gap-2 rounded-md border border-neutral-600 bg-neutral-900 px-2 text-xs text-white">
                <Checkbox
                  checked={Boolean(scanSrc) && isBlankVisible}
                  disabled={!scanSrc}
                  onCheckedChange={(checked) =>
                    setIsBlankVisible(checked === true)
                  }
                />
                <span>Показувати бланк</span>
              </label>
            )}
            {calibration && showCalibrationControls && (
              <div className="w-full space-y-3 rounded-md border border-neutral-600 bg-neutral-900 px-2 pt-2 pb-4 text-white">
                <div className="text-xs font-medium text-white">
                  Застосувати для всіх полів:
                </div>
                {(
                  [
                    ["x", "Зміщення по X", currentOffsets.x],
                    ["y", "Зміщення по Y", currentOffsets.y],
                  ] as const
                ).map(([axis, label, value]) => (
                  <div className="w-full space-y-1" key={axis}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span>{label}</span>
                      <output className="text-neutral-300">
                        {formatOffset(value)}
                      </output>
                    </div>
                    <Slider
                      aria-label={label}
                      className="px-1 [&_[data-slot=slider-range]]:bg-primary [&_[data-slot=slider-thumb]]:border-white [&_[data-slot=slider-track]]:bg-neutral-700"
                      max={offsetMaxMm}
                      min={offsetMinMm}
                      onValueChange={(nextValue) =>
                        updateOffset(axis, nextValue[0] ?? 0)
                      }
                      step={offsetStepMm}
                      value={[value]}
                    />
                  </div>
                ))}
                <div className="w-full space-y-1">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span>Масштабування</span>
                    <output className="text-neutral-300">
                      {currentElementScale.toFixed(2)}
                    </output>
                  </div>
                  <Slider
                    aria-label="Масштабування"
                    className="px-1 [&_[data-slot=slider-range]]:bg-primary [&_[data-slot=slider-thumb]]:border-white [&_[data-slot=slider-track]]:bg-neutral-700"
                    max={elementScaleMax}
                    min={elementScaleMin}
                    onValueChange={(nextValue) =>
                      updateElementScale(nextValue[0] ?? 1)
                    }
                    step={elementScaleStep}
                    value={[currentElementScale]}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="absolute right-3 bottom-3 z-40 flex flex-wrap items-end justify-end gap-1 print:hidden"
        data-preview-interactive="true"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Button
          aria-label="Зменшити масштаб"
          className="!border-neutral-600 !bg-neutral-900 !text-white hover:!bg-neutral-800 hover:!text-white disabled:opacity-100"
          disabled={zoom <= minZoom}
          onClick={() => updateZoom(zoom - zoomStep)}
          size="icon"
          title="Зменшити масштаб"
          variant="outline"
        >
          <Minus />
        </Button>
        <Button
          aria-label={`Масштаб ${Math.round(zoom * 100)}%`}
          className="min-w-14 !border-neutral-600 !bg-neutral-900 px-2 !text-white hover:!bg-neutral-800 hover:!text-white disabled:opacity-100"
          onClick={() => updateZoom(1)}
          title="Скинути масштаб до 100%"
          variant="outline"
        >
          {Math.round(zoom * 100)}%
        </Button>
        <Button
          aria-label="Збільшити масштаб"
          className="!border-neutral-600 !bg-neutral-900 !text-white hover:!bg-neutral-800 hover:!text-white disabled:opacity-100"
          disabled={zoom >= maxZoom}
          onClick={() => updateZoom(zoom + zoomStep)}
          size="icon"
          title="Збільшити масштаб"
          variant="outline"
        >
          <Plus />
        </Button>
        <Button
          aria-label="Центрувати документ"
          className="!border-neutral-600 !bg-neutral-900 !text-white hover:!bg-neutral-800 hover:!text-white disabled:opacity-100"
          onClick={() => {
            setPan({ x: 0, y: 0 })
            viewportRef.current?.scrollTo({
              left: Math.max(
                0,
                (viewportRef.current.scrollWidth -
                  viewportRef.current.clientWidth) /
                  2
              ),
              top: Math.max(
                0,
                (viewportRef.current.scrollHeight -
                  viewportRef.current.clientHeight) /
                  2
              ),
            })
          }}
          size="icon"
          title="Центрувати документ"
          variant="outline"
        >
          <LocateFixed />
        </Button>
      </div>
      {showFieldList && (
        <aside
          className={cn(
            "absolute inset-y-5 left-0 z-30 bg-transparent transition-[width] duration-200 print:hidden",
            isFieldListExpanded
              ? "w-[300px] overflow-visible"
              : "w-11 overflow-visible"
          )}
          data-preview-interactive="true"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <Button
            aria-expanded={isFieldListExpanded}
            aria-label={
              isFieldListExpanded
                ? "Згорнути список полів"
                : "Розгорнути список полів"
            }
            className="absolute -top-2 left-3 z-10 w-max gap-2 !bg-transparent px-2 whitespace-nowrap text-white hover:bg-white/15 hover:text-white aria-expanded:!bg-transparent aria-expanded:!text-white"
            onClick={() => setIsFieldListExpanded((expanded) => !expanded)}
            size="default"
            title={isFieldListExpanded ? "Згорнути" : "Розгорнути"}
            variant="ghost"
          >
            {isFieldListExpanded ? (
              <>
                <ChevronLeft />
                <span>Сховати список полів</span>
              </>
            ) : (
              <>
                <Menu />
                <span>Показати список полів</span>
              </>
            )}
          </Button>
          {isFieldListExpanded && (
            <ol className="h-full space-y-2 overflow-y-auto pt-12">
              {protocolV2PrintedFields.map((field) => {
                const configuredField = layoutFields.find(
                  (layoutField) => layoutField.id === field.id
                )
                const isEnabled = configuredField?.enabled !== false
                const fieldLabel = protocolV2FieldLabels[field.id] ?? field.id

                return (
                  <li
                    className="mx-3 flex items-center gap-2 rounded-md border border-neutral-600 bg-neutral-900 px-3 py-2 text-xs text-white shadow-none transition-colors duration-200 hover:bg-neutral-800"
                    key={field.id}
                  >
                    <Switch
                      aria-label={`Показувати поле: ${fieldLabel}`}
                      checked={isEnabled}
                      disabled={!calibration}
                      onCheckedChange={(checked) =>
                        updateField({
                          ...(configuredField ?? field),
                          enabled: checked,
                        })
                      }
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">{fieldLabel}</span>
                  </li>
                )
              })}
            </ol>
          )}
        </aside>
      )}
    </div>
  )
}
